import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApiError } from '../api/client';
import { useIncomingAttacks, useWorldOptions } from '../api/queries';
import {
  ATTACK_SIZE_ICON,
  ATTACK_SIZE_LABEL,
  type DopplerTarget,
  type IncomingAttack,
  type IncomingAttackVillageRef,
  type User,
} from '../api/types';
import { Spinner } from '../components/Spinner';
import { useDebounced } from '../hooks/useDebounced';

/** Kurzform je Einheitentyp, wie in der Berichtsansicht. */
const OFF_UNIT_LABELS = { axe: 'Axt', light: 'LKav', ram: 'Ramme' } as const;

/** Rundet auf volle Zehner -- eine Hochrechnung ist ohnehin nur eine Schätzung, falsche Präzision wäre irreführend. */
function roundTroopCount(value: number | undefined): number {
  return Math.round((value ?? 0) / 10) * 10;
}

/** Hover-Text des Angriffsgrößen-Icons: die tatsächliche Truppenhochrechnung. */
function troopProjectionTooltip(projection: NonNullable<IncomingAttack['troopProjection']>): string {
  const troops = (['axe', 'light', 'ram'] as const)
    .map((unit) => `~${roundTroopCount(projection.projected[unit])} ${OFF_UNIT_LABELS[unit]}`)
    .join(' / ');
  const label =
    projection.basis === 'evidence' ? `Mögliche Truppen: ${troops}` : `Keine Truppendaten — volle Off möglich: ${troops}`;
  return `${ATTACK_SIZE_LABEL[projection.attackSize]}\n${label}`;
}

function villageLabel(ref: IncomingAttackVillageRef): string {
  if (ref.village) return ref.village;
  if (ref.coordinates) return `(${ref.coordinates.x}|${ref.coordinates.y})`;
  return 'Unbekannt';
}

/** `H:MM:SS`, negativ (bereits angekommen) als "jetzt". */
function formatCountdown(arrivesAt: string, now: number): string {
  const diffMs = new Date(arrivesAt).getTime() - now;
  if (diffMs <= 0) return 'jetzt';

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

/** Ziele + Ankunftszeiten eines Dopplers, als Zeilen für den Tooltip (title-Attribut, kein eigenes Popover nötig). */
function dopplerTooltip(targets: DopplerTarget[]): string {
  return targets
    .map((target) => {
      const village = target.village ?? (target.coordinates ? `(${target.coordinates.x}|${target.coordinates.y})` : 'Unbekannt');
      return `${village} — Ankunft ${new Date(target.arrivesAt).toLocaleString('de-DE')}`;
    })
    .join('\n');
}

/**
 * "Aktuelle Angriffe": eine gemeinsame Übersicht je Welt, gespeist aus dem
 * automatischen Upload des Userscripts von der "Eingehende Befehle"-Seite
 * (`screen=overview_villages&mode=incomings`). Jeder Upload bringt den
 * gespeicherten Stand des jeweiligen Kontos vollständig auf die
 * hochgeladenen Befehle -- mehrere Kontos derselben Welt tragen gemeinsam
 * zu dieser Liste bei. "Nur eigene" und die Textfilter greifen wie bei den
 * Berichten auf Angreifer UND Verteidiger (Besitzer des Zieldorfs).
 */
export function IncomingAttacksPage({ user }: { user: User }) {
  const [onlyOwn, setOnlyOwn] = useState(true);
  const [target, setTarget] = useState('');
  const [origin, setOrigin] = useState('');
  const [attackerFilter, setAttackerFilter] = useState('');
  const [defenderFilter, setDefenderFilter] = useState('');

  const needsWorldChoice = user.world === null;
  const [worldKey, setWorldKey] = useState('');
  const worlds = useWorldOptions('login');
  const activeWorld = user.world?.key ?? worldKey;

  const debouncedTarget = useDebounced(target);
  const debouncedOrigin = useDebounced(origin);
  const debouncedAttacker = useDebounced(attackerFilter);
  const debouncedDefender = useDebounced(defenderFilter);

  const attacks = useIncomingAttacks({
    welt: needsWorldChoice ? worldKey : undefined,
    eigene: onlyOwn,
    ...(debouncedTarget ? { ziel: debouncedTarget } : {}),
    ...(debouncedOrigin ? { herkunft: debouncedOrigin } : {}),
    ...(debouncedAttacker ? { angreifer: debouncedAttacker } : {}),
    ...(debouncedDefender ? { verteidiger: debouncedDefender } : {}),
  });

  const hasGamePlayer = attacks.data?.hasGamePlayer ?? Boolean(user.gamePlayerId ?? user.gamePlayerName);
  const rows = attacks.data?.attacks ?? [];

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h1>Aktuelle Angriffe</h1>
          <p className="muted">
            Eingehende Befehle, wie sie das Userscript automatisch von der Spielseite hochlädt —
            eine gemeinsame Liste für alle Kontos dieser Welt.
          </p>
        </div>
      </div>

      {needsWorldChoice && (
        <label className="field">
          <span>Welt</span>
          <select value={worldKey} onChange={(event) => setWorldKey(event.target.value)} required>
            <option value="" disabled>
              Welt wählen …
            </option>
            {(worlds.data?.worlds ?? []).map((world) => (
              <option key={world.key} value={world.key}>
                {world.name}
              </option>
            ))}
          </select>
        </label>
      )}

      {needsWorldChoice && !worldKey && (
        <p className="muted center pad">Wähle eine Welt, um ihre eingehenden Angriffe zu sehen.</p>
      )}

      {activeWorld && (
        <>
          <div className="filter-bar">
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={onlyOwn}
                onChange={(event) => setOnlyOwn(event.target.checked)}
              />
              <span>Nur eigene Angriffe anzeigen</span>
            </label>
            <input
              type="text"
              placeholder="Ziel"
              value={target}
              onChange={(event) => setTarget(event.target.value)}
            />
            <input
              type="text"
              placeholder="Herkunft"
              value={origin}
              onChange={(event) => setOrigin(event.target.value)}
            />
            <input
              type="text"
              placeholder="Angreifer"
              value={attackerFilter}
              onChange={(event) => setAttackerFilter(event.target.value)}
            />
            <input
              type="text"
              placeholder="Verteidiger"
              value={defenderFilter}
              onChange={(event) => setDefenderFilter(event.target.value)}
            />
          </div>

          {onlyOwn && !hasGamePlayer && (
            <div className="flash flash-info">
              Ohne hinterlegte Spielfigur lassen sich keine eigenen Angriffe erkennen. Trage sie
              unter <Link to="/konto">Mein Konto</Link> ein, oder schalte den Haken oben aus.
            </div>
          )}

          {attacks.isPending && <Spinner label="Liste wird geladen …" />}

          {attacks.isError && (
            <div className="flash flash-error">
              {attacks.error instanceof ApiError
                ? attacks.error.message
                : 'Liste konnte nicht geladen werden.'}
            </div>
          )}

          {!attacks.isPending && rows.length === 0 && (
            <p className="muted center pad">Keine eingehenden Angriffe gefunden.</p>
          )}

          {rows.length > 0 && (
            <div className={`table-wrap ${attacks.isFetching ? 'is-refreshing' : ''}`}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Doppler</th>
                    <th>Status</th>
                    <th>Größe</th>
                    <th>Ziel</th>
                    <th>Herkunft</th>
                    <th>Angreifer</th>
                    <th>Verteidiger</th>
                    <th>Entfernung</th>
                    <th>Ankunft</th>
                    <th>Gemeldet von</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((attack: IncomingAttack) => (
                    <tr key={attack.id}>
                      <td>
                        {attack.dopplerCount >= 2 && attack.dopplerTargets && (
                          <span
                            className="badge badge-danger"
                            title={`Mehrere Angriffe aus demselben Dorf:\n${dopplerTooltip(attack.dopplerTargets)}`}
                          >
                            Doppler ({attack.dopplerCount}x)
                          </span>
                        )}
                      </td>
                      <td>
                        {attack.troopProjection?.knownOff && <span className="badge badge-danger">Off-Dorf</span>}
                        {attack.troopProjection?.suspectedDeff && <span className="badge badge-active">Deff-Dorf</span>}
                      </td>
                      <td className="center">
                        {attack.troopProjection && (
                          <img
                            src={ATTACK_SIZE_ICON[attack.troopProjection.attackSize]}
                            alt={ATTACK_SIZE_LABEL[attack.troopProjection.attackSize]}
                            title={troopProjectionTooltip(attack.troopProjection)}
                            className="report-icon"
                          />
                        )}
                      </td>
                      <td>{villageLabel(attack.target)}</td>
                      <td>{villageLabel(attack.origin)}</td>
                      <td>{attack.attacker.name ?? 'Unbekannt'}</td>
                      <td>{attack.defender.name ?? 'Unbekannt'}</td>
                      <td className="nowrap">{attack.distance !== null ? attack.distance.toFixed(1) : '—'}</td>
                      <td className="nowrap" title={new Date(attack.arrivesAt).toLocaleString('de-DE')}>
                        {formatCountdown(attack.arrivesAt, now)}
                      </td>
                      <td className="muted">{attack.createdByName ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
